<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <div class="orangehrm-header-container">
        <oxd-text tag="h6" class="orangehrm-main-title">
          {{ $t('admin.leave_slack_notifications') }}
        </oxd-text>
        <oxd-switch-input
          v-model="configuration.enabled"
          label-position="left"
          :option-label="$t('general.enable')"
        />
      </div>

      <oxd-text
        v-if="configuration.source === 'environment'"
        tag="p"
        class="orangehrm-input-hint"
      >
        {{ $t('admin.leave_slack_notifications_env_override_hint') }}
      </oxd-text>

      <oxd-divider />

      <oxd-form :loading="isLoading" @submit-valid="onSubmit">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.webhookUrl"
                :label="$t('admin.slack_webhook_url')"
                type="password"
                :rules="webhookUrlRules"
                :required="configuration.enabled || submitAction === 'test'"
              />
              <oxd-text tag="p" class="orangehrm-input-hint">
                {{ $t('admin.leave_slack_notifications_webhook_hint') }}
              </oxd-text>
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.channel"
                :label="$t('admin.slack_channel')"
                :rules="rules.channel"
                placeholder="#general"
              />
              <oxd-text tag="p" class="orangehrm-input-hint">
                {{ $t('admin.leave_slack_notifications_channel_hint') }}
              </oxd-text>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.sendTime"
                label="Daily Send Time"
                type="time"
                :rules="rules.sendTime"
                required
              />
              <oxd-text tag="p" class="orangehrm-input-hint">
                Daily leave summaries will be sent at this time.
              </oxd-text>
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.timezoneName"
                label="Timezone"
                :rules="rules.timezoneName"
                placeholder="America/Chicago"
                required
              />
              <oxd-text tag="p" class="orangehrm-input-hint">
                Use an IANA timezone, such as {{ browserTimezone }}.
              </oxd-text>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item class="orangehrm-slack-toggle-row">
              <oxd-text tag="p" class="orangehrm-slack-toggle-label">
                Immediate Leave Type Notifications
              </oxd-text>
              <oxd-switch-input v-model="configuration.immediateEnabled" />
            </oxd-grid-item>
            <oxd-grid-item class="orangehrm-slack-toggle-row">
              <oxd-text tag="p" class="orangehrm-slack-toggle-label">
                Only Send After Approval
              </oxd-text>
              <oxd-switch-input
                v-model="configuration.immediateApprovedOnly"
                :disabled="!configuration.immediateEnabled"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item class="orangehrm-slack-toggle-row">
              <div class="orangehrm-slack-toggle-copy">
                <oxd-text tag="p" class="orangehrm-slack-toggle-label">
                  Send Same-Day Cancellation Notifications
                </oxd-text>
                <oxd-text tag="p" class="orangehrm-input-hint">
                  Only posts to Slack when the cancelled leave includes today in
                  the configured timezone.
                </oxd-text>
              </div>
              <oxd-switch-input
                v-model="configuration.immediateSameDayCancellationEnabled"
                :disabled="!configuration.immediateEnabled"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-text tag="p" class="orangehrm-optional-field-label">
                Immediate Leave Types
              </oxd-text>
              <oxd-text tag="p" class="orangehrm-input-hint">
                Turn on the leave types that should send Slack immediately when
                they are requested or approved for leave that includes today in
                the configured timezone.
              </oxd-text>
              <oxd-text tag="p" class="orangehrm-input-hint">
                Leave types without a custom message use
                <code>&#123;&#123;fullName&#125;&#125;</code> is out
                <code>&#123;&#123;leaveTypeLower&#125;&#125;</code> today.
                Available placeholders:
                <code>&#123;&#123;firstName&#125;&#125;</code>,
                <code>&#123;&#123;lastName&#125;&#125;</code>,
                <code>&#123;&#123;fullName&#125;&#125;</code>,
                <code>&#123;&#123;slack&#125;&#125;</code>,
                <code>&#123;&#123;leaveType&#125;&#125;</code>,
                <code>&#123;&#123;leaveTypeLower&#125;&#125;</code>,
                <code>&#123;&#123;date&#125;&#125;</code>,
                <code>&#123;&#123;dateRange&#125;&#125;</code>,
                <code>&#123;&#123;duration&#125;&#125;</code>,
                <code>&#123;&#123;timeOff&#125;&#125;</code>, and
                <code>&#123;&#123;status&#125;&#125;</code>.
              </oxd-text>
              <div class="orangehrm-leave-type-list">
                <div
                  v-if="availableLeaveTypes.length === 0"
                  class="orangehrm-leave-type-empty"
                >
                  No active leave types available.
                </div>
                <div
                  v-for="leaveType in availableLeaveTypes"
                  :key="leaveType.id"
                  class="orangehrm-leave-type-row"
                >
                  <div class="orangehrm-leave-type-header">
                    <oxd-text tag="p" class="orangehrm-leave-type-label">
                      {{ leaveType.name }}
                    </oxd-text>
                    <oxd-switch-input
                      :model-value="isImmediateLeaveTypeEnabled(leaveType.id)"
                      :disabled="!configuration.immediateEnabled"
                      @update:model-value="
                        setImmediateLeaveTypeEnabled(leaveType.id, $event)
                      "
                    />
                  </div>
                  <oxd-input-field
                    :model-value="getImmediateMessageTemplate(leaveType.id)"
                    type="textarea"
                    label="Immediate Slack Message"
                    :disabled="
                      !configuration.immediateEnabled ||
                      !isImmediateLeaveTypeEnabled(leaveType.id)
                    "
                    :placeholder="defaultImmediateMessageTemplate"
                    @update:model-value="
                      setImmediateMessageTemplate(leaveType.id, $event)
                    "
                  />
                </div>
              </div>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item class="orangehrm-slack-toggle-row">
              <div class="orangehrm-slack-toggle-copy">
                <oxd-text tag="p" class="orangehrm-slack-toggle-label">
                  Weekly Monday Leave Summary
                </oxd-text>
                <oxd-text tag="p" class="orangehrm-input-hint">
                  Every Monday at the configured send time, posts a summary of
                  staff on leave this week and next week to Slack.
                </oxd-text>
              </div>
              <div class="orangehrm-weekly-summary-controls">
                <oxd-button
                  type="submit"
                  display-type="ghost"
                  label="Send Test"
                  :disabled="!configuration.webhookUrl"
                  @click="setSubmitAction('testWeekly')"
                />
                <oxd-switch-input
                  v-model="configuration.weeklySummaryEnabled"
                  :disabled="!configuration.enabled"
                />
              </div>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <div class="orangehrm-actions-group">
            <oxd-button
              type="button"
              display-type="ghost"
              :label="$t('general.reset')"
              @click="onReset"
            />
            <oxd-button
              type="submit"
              display-type="secondary"
              label="Test Today's Summary"
              @click="setSubmitAction('test')"
            />
            <oxd-button
              type="submit"
              display-type="secondary"
              :label="$t('general.save')"
              @click="setSubmitAction('save')"
            />
          </div>
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {APIService} from '@ohrm/core/util/services/api.service';
import {
  required,
  shouldNotExceedCharLength,
} from '@ohrm/core/util/validation/rules';
import {OxdSwitchInput} from '@ohrm/oxd';

const defaultConfiguration = () => ({
  enabled: false,
  webhookUrl: '',
  channel: '',
  sendTime: '09:00',
  timezoneName: 'UTC',
  immediateEnabled: false,
  immediateLeaveTypeIds: [],
  immediateApprovedOnly: false,
  immediateSameDayCancellationEnabled: false,
  immediateMessageTemplates: {},
  weeklySummaryEnabled: false,
  source: 'none',
});

const defaultImmediateMessageTemplate =
  '{{fullName}} is out {{leaveTypeLower}} today.';

const cloneConfiguration = (configuration) => ({
  ...configuration,
  immediateLeaveTypeIds: [...(configuration.immediateLeaveTypeIds ?? [])],
  immediateMessageTemplates: {
    ...(configuration.immediateMessageTemplates ?? {}),
  },
});

const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const validSendTime = (value) => {
  if (!value) {
    return true;
  }

  return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value)
    ? true
    : 'Enter a valid time in 24-hour format.';
};

const validTimezoneName = (value) => {
  if (!value) {
    return true;
  }

  try {
    Intl.DateTimeFormat(undefined, {timeZone: value});
    return true;
  } catch {
    return 'Enter a valid timezone, such as America/Chicago.';
  }
};

export default {
  components: {
    'oxd-switch-input': OxdSwitchInput,
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/admin/leave-slack-notification',
    );

    return {
      http,
    };
  },

  data() {
    const browserTimezone = getBrowserTimezone();

    return {
      browserTimezone,
      defaultImmediateMessageTemplate,
      availableLeaveTypes: [],
      isLoading: false,
      configuration: defaultConfiguration(),
      initialConfiguration: defaultConfiguration(),
      submitAction: 'save',
      rules: {
        channel: [shouldNotExceedCharLength(100)],
        sendTime: [required, validSendTime],
        timezoneName: [
          required,
          shouldNotExceedCharLength(255),
          validTimezoneName,
        ],
      },
    };
  },

  computed: {
    webhookUrlRules() {
      const rules = [shouldNotExceedCharLength(255), this.validWebhookUrl];

      if (this.configuration.enabled || this.submitAction === 'test') {
        rules.unshift(required);
      }

      return rules;
    },
  },

  created() {
    this.loadConfiguration();
  },

  methods: {
    loadConfiguration() {
      this.isLoading = true;
      this.http
        .getAll()
        .then((response) => {
          const {configuration, availableLeaveTypes} = this.extractResponseData(
            response.data.data,
          );
          this.availableLeaveTypes = availableLeaveTypes;
          this.configuration = cloneConfiguration(configuration);
          this.initialConfiguration = cloneConfiguration(configuration);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    normalizeConfiguration(configuration) {
      const normalizedConfiguration = {
        ...defaultConfiguration(),
        ...configuration,
      };

      normalizedConfiguration.immediateLeaveTypeIds = Array.isArray(
        normalizedConfiguration.immediateLeaveTypeIds,
      )
        ? normalizedConfiguration.immediateLeaveTypeIds.map((leaveTypeId) =>
            Number(leaveTypeId),
          )
        : [];
      normalizedConfiguration.immediateMessageTemplates =
        this.normalizeImmediateMessageTemplates(
          normalizedConfiguration.immediateMessageTemplates,
        );

      if (
        normalizedConfiguration.source === 'none' &&
        normalizedConfiguration.timezoneName === 'UTC' &&
        this.browserTimezone !== ''
      ) {
        normalizedConfiguration.timezoneName = this.browserTimezone;
      }

      return normalizedConfiguration;
    },

    extractResponseData(data) {
      const {availableLeaveTypes = [], ...configurationData} = data ?? {};

      return {
        configuration: this.normalizeConfiguration(configurationData),
        availableLeaveTypes,
      };
    },

    normalizeImmediateMessageTemplates(messageTemplates) {
      if (!messageTemplates || typeof messageTemplates !== 'object') {
        return {};
      }

      return Object.entries(messageTemplates).reduce(
        (normalizedMessageTemplates, [leaveTypeId, template]) => {
          const normalizedLeaveTypeId = Number(leaveTypeId);
          if (
            !Number.isFinite(normalizedLeaveTypeId) ||
            typeof template !== 'string'
          ) {
            return normalizedMessageTemplates;
          }

          normalizedMessageTemplates[String(normalizedLeaveTypeId)] = template;
          return normalizedMessageTemplates;
        },
        {},
      );
    },

    validWebhookUrl(value) {
      if (!value) {
        return true;
      }

      try {
        new URL(value);
        return true;
      } catch {
        return this.$t('admin.enter_a_valid_webhook_url');
      }
    },

    getResolvedTimezoneName() {
      return (
        this.configuration.timezoneName?.trim() || this.browserTimezone || 'UTC'
      );
    },

    isImmediateLeaveTypeEnabled(leaveTypeId) {
      return this.configuration.immediateLeaveTypeIds.includes(leaveTypeId);
    },

    getImmediateMessageTemplate(leaveTypeId) {
      return (
        this.configuration.immediateMessageTemplates[String(leaveTypeId)] ?? ''
      );
    },

    setImmediateMessageTemplate(leaveTypeId, template) {
      this.configuration.immediateMessageTemplates = {
        ...this.configuration.immediateMessageTemplates,
        [String(leaveTypeId)]: template,
      };
    },

    setImmediateLeaveTypeEnabled(leaveTypeId, enabled) {
      const leaveTypeIds = new Set(this.configuration.immediateLeaveTypeIds);
      if (enabled) {
        leaveTypeIds.add(leaveTypeId);
        if (this.getImmediateMessageTemplate(leaveTypeId) === '') {
          this.setImmediateMessageTemplate(
            leaveTypeId,
            this.defaultImmediateMessageTemplate,
          );
        }
      } else {
        leaveTypeIds.delete(leaveTypeId);
      }

      this.configuration.immediateLeaveTypeIds = Array.from(leaveTypeIds).sort(
        (leftId, rightId) => leftId - rightId,
      );
    },

    setSubmitAction(action) {
      this.submitAction = action;
    },

    onReset() {
      this.configuration = cloneConfiguration(this.initialConfiguration);
      this.submitAction = 'save';
    },

    onSubmit() {
      this.isLoading = true;
      const sendTestSummary = this.submitAction === 'test';
      const sendTestWeeklySummary = this.submitAction === 'testWeekly';
      this.http
        .request({
          method: 'PUT',
          url: '/api/v2/admin/leave-slack-notification',
          data: {
            enabled: this.configuration.enabled,
            webhookUrl: this.configuration.webhookUrl,
            channel: this.configuration.channel,
            sendTime: this.configuration.sendTime,
            timezoneName: this.getResolvedTimezoneName(),
            immediateEnabled: this.configuration.immediateEnabled,
            immediateLeaveTypeIds: this.configuration.immediateLeaveTypeIds,
            immediateApprovedOnly: this.configuration.immediateApprovedOnly,
            immediateSameDayCancellationEnabled:
              this.configuration.immediateSameDayCancellationEnabled,
            immediateMessageTemplates:
              this.configuration.immediateMessageTemplates,
            weeklySummaryEnabled: this.configuration.weeklySummaryEnabled,
            sendTestSummary,
            sendTestWeeklySummary,
          },
        })
        .then((response) => {
          const {configuration, availableLeaveTypes} = this.extractResponseData(
            response.data.data,
          );
          this.availableLeaveTypes = availableLeaveTypes;
          this.configuration = cloneConfiguration(configuration);
          this.initialConfiguration = cloneConfiguration(configuration);

          if (sendTestSummary) {
            const testSummaryStatus = response.data.meta?.testSummaryStatus;
            if (testSummaryStatus === 'sent') {
              return this.$toast.success({
                title: this.$t('general.success'),
                message: "Today's leave summary was sent to Slack.",
              });
            }
            if (testSummaryStatus === 'empty') {
              return this.$toast.info({
                title: this.$t('general.info'),
                message: 'Nobody scheduled for leave today.',
              });
            }

            return this.$toast.warn({
              title: this.$t('general.failed'),
              message:
                'Test leave summary could not be delivered. Please verify the webhook URL, channel, and timezone.',
            });
          }

          if (sendTestWeeklySummary) {
            const status = response.data.meta?.testWeeklySummaryStatus;
            if (status === 'sent') {
              return this.$toast.success({
                title: this.$t('general.success'),
                message:
                  'Weekly leave summary (this week + next week) was sent to Slack.',
              });
            }
            if (status === 'empty') {
              return this.$toast.info({
                title: this.$t('general.info'),
                message: 'No staff on leave this week or next week.',
              });
            }

            return this.$toast.warn({
              title: this.$t('general.failed'),
              message:
                'Weekly summary could not be delivered. Please verify the webhook URL, channel, and timezone.',
            });
          }

          return this.$toast.updateSuccess();
        })
        .finally(() => {
          this.isLoading = false;
          this.submitAction = 'save';
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-actions-group {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.orangehrm-slack-toggle-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
}

.orangehrm-slack-toggle-label {
  @include oxd-input-control();
  padding: 0;
  flex-basis: 75%;
}

.orangehrm-slack-toggle-copy {
  flex: 1;
}

.orangehrm-leave-type-list {
  border: 1px solid $oxd-interface-gray-lighten-2-color;
  border-radius: 0.5rem;
  overflow: hidden;
}

.orangehrm-leave-type-row {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid $oxd-interface-gray-lighten-2-color;
}

.orangehrm-leave-type-row:last-child {
  border-bottom: none;
}

.orangehrm-leave-type-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.orangehrm-leave-type-label {
  margin-right: 1rem;
}

.orangehrm-leave-type-empty {
  padding: 0.75rem 1rem;
  color: $oxd-interface-gray-color;
}

.orangehrm-weekly-summary-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}
</style>
